import React from "react";
import { Dropzone } from "@mantine/dropzone";
import {
  Button,
  Progress,
  Text,
  ActionIcon,
  Checkbox,
  Anchor,
  Modal,
  Tooltip,
} from "@mantine/core";
import { BeatLoader } from "react-spinners";
import { showNotification } from "@mantine/notifications";
import { Trash } from "tabler-icons-react";
import { IconDownload } from "@tabler/icons-react";
import colors from "../../utilities/design";
import { loadingStates } from "../../utilities/utilities";
import { useSelector } from "react-redux";
import { selectUserData } from "../../redux/selectors";

const BRAND_SKY = "text-[#46BDE1]";
const BRAND_BORDER = "border-[#46BDE1]";

const FileRowSkeleton = ({ showActions = true }) => (
  <div className="mt-4 flex w-full animate-pulse items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 p-4">
    <div className="h-10 w-10 shrink-0 rounded-lg bg-gray-200" />
    <div className="flex min-w-0 flex-1 flex-col gap-2.5">
      <div className="h-4 w-2/5 max-w-xs rounded bg-gray-200" />
      <div className="h-2 w-full rounded-full bg-gray-200" />
    </div>
    {showActions && (
      <div className="flex shrink-0 items-center gap-2">
        <div className="hidden h-9 w-24 rounded-lg bg-gray-200 sm:block" />
        <div className="h-9 w-9 rounded-lg bg-gray-200" />
        <div className="h-9 w-9 rounded-lg bg-gray-200" />
      </div>
    )}
  </div>
);

const FileListSkeleton = ({ count = 3, showActions = true }) => (
  <div className="w-full">
    {Array.from({ length: count }).map((_, i) => (
      <FileRowSkeleton key={i} showActions={showActions} />
    ))}
  </div>
);

const FileActions = ({
  file,
  index,
  fileNameHidden,
  hideOpenButton,
  showDownload,
  downloadFile,
  saveDownloadFile,
}) => {
  if (!file.uploaded || hideOpenButton) return null;

  const isOpening = file.downloading === loadingStates.LOADING;
  const isSaving = file.saving === loadingStates.LOADING;

  return (
    <>
      {!fileNameHidden ? (
        <Button
          onClick={downloadFile(index)}
          className={`mx-2 w-36 shrink-0 sm:w-48 ${BRAND_BORDER} ${BRAND_SKY}`}
          disabled={isOpening}
          variant="outline"
        >
          {isOpening ? (
            <BeatLoader size={10} color={colors.primary} />
          ) : (
            <Text>Open</Text>
          )}
        </Button>
      ) : (
        <Anchor
          onClick={downloadFile(index)}
          className={`mx-2 min-w-0 max-w-xs truncate ${BRAND_SKY}`}
          disabled={isOpening}
        >
          {isOpening ? (
            <BeatLoader size={10} color={colors.primary} />
          ) : (
            <Text className="truncate">{file.fileName}</Text>
          )}
        </Anchor>
      )}

      {showDownload && (
        <Tooltip label="Download file" position="top" withArrow>
          <ActionIcon
            onClick={saveDownloadFile(index)}
            disabled={isSaving}
            className="mx-1 shrink-0"
            variant="subtle"
          >
            {isSaving ? (
              <BeatLoader size={6} color={colors.primary} />
            ) : (
              <IconDownload size={18} className={BRAND_SKY} />
            )}
          </ActionIcon>
        </Tooltip>
      )}
    </>
  );
};

const NewDocumentUploader = ({
  multiUploadArgs,
  hideDropZone,
  hideDeleteButton,
  hideOpenButton,
  checkBoxShow = false,
  fileNameHidden = false,
}) => {
  const currentUser = useSelector(selectUserData) || {};
  const [open, setOpen] = React.useState(false);
  const [deleteIndex, setDeleteIndex] = React.useState(0);
  const {
    addFiles,
    deleteFile,
    checkFile,
    downloadFile,
    saveDownloadFile,
    files,
    loadingFromParent,
  } = multiUploadArgs;

  const isLoading = loadingFromParent === loadingStates.LOADING;
  const isEmpty = files.length === 0 && !isLoading;
  const skeletonCount = Math.max(files.length, 1);
  const isEditor = currentUser.loginAs?.id === 4;
  const isClientAdmin = currentUser.loginAs?.id === 2;

  return (
    <div className="flex w-full flex-col pt-4">
      {!hideDropZone && !isLoading && !isClientAdmin && (
        <Dropzone
          onDrop={addFiles}
          onReject={(filesIncoming) =>
            showNotification({
              color: "red",
              title: "Files rejected",
              message: `${filesIncoming.length} files rejected.`,
            })
          }
          maxSize={1024 * 1024 * 25}
          className="mt-1 rounded-2xl border border-dashed border-[#548CA8]/25 transition-colors hover:border-[#46BDE1]/40 bg-[#548CA8]/15"
        >
          <div className="flex h-[8.875rem] flex-col items-center justify-center px-4 text-center">
            <img
              src="/assets/images/download.svg"
              alt=""
              className="h-10 w-10 opacity-80"
            />
            <p className="mt-3 text-sm text-gray-600">
              Drop or select files up to 25MB each
            </p>
          </div>
        </Dropzone>
      )}

      {isLoading && (
        <FileListSkeleton
          count={skeletonCount}
          showActions={!hideOpenButton || !fileNameHidden}
        />
      )}

      {!isLoading &&
        files.map((file, index) => (
          <div
            key={`${file.fileName}-${index}`}
            className={`flex w-full flex-col gap-3 rounded-xl border border-gray-100 bg-white p-4 shadow-sm sm:flex-row sm:items-center ${
              index === 0 ? "mt-4" : "mt-4"
            }`}
          >
            <div className="flex min-w-0 flex-1 items-center gap-3">
              {checkBoxShow && file.uploaded && (
                <Checkbox
                  color="dark"
                  size="md"
                  className="shrink-0 cursor-pointer"
                  checked={file.checked}
                  onChange={checkFile(index)}
                />
              )}

              <img
                src="/assets/images/downloadFileLogo.svg"
                alt=""
                className="h-10 w-10 shrink-0"
              />

              {!fileNameHidden && (
                <div className="flex min-w-0 flex-1 flex-col">
                  <p className="truncate text-sm font-medium tracking-tight text-[#191D23]">
                    {file.fileName}
                  </p>
                  <div className="mt-2.5">
                    <Progress
                      color={file.errored ? "red" : "teal"}
                      size="sm"
                      animate={
                        file.fetchingUploadUrl ||
                        file.attaching === loadingStates.LOADING
                      }
                      value={
                        file.fetchingUploadUrl || file.errored
                          ? 100
                          : file.uploadPercentage
                      }
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex shrink-0 items-center justify-end gap-1 sm:pl-2">
              <FileActions
                file={file}
                index={index}
                fileNameHidden={fileNameHidden}
                hideOpenButton={hideOpenButton}
                showDownload={isEditor || isClientAdmin}
                downloadFile={downloadFile}
                saveDownloadFile={saveDownloadFile}
              />

              {(file.uploaded || file.errored) &&
                !hideDeleteButton &&
                currentUser.loginAs?.id !== 2 && (
                  <ActionIcon
                    color="red"
                    variant="subtle"
                    className="shrink-0"
                    onClick={() => {
                      setDeleteIndex(index);
                      setOpen(true);
                    }}
                  >
                    <Trash size={18} />
                  </ActionIcon>
                )}
            </div>
          </div>
        ))}

      <Modal
        overflow="inside"
        closeOnClickOutside={false}
        size="calc(30vw)"
        opened={open}
        onClose={() => setOpen(false)}
        title="Delete file"
        classNames={{ title: "font-semibold" }}
      >
        <div className="my-2">
          <Text className="mb-6 text-center text-gray-600">
            Are you sure you want to delete this file?
          </Text>
          <div className="flex flex-row justify-between gap-3">
            <Button
              className="flex-1 rounded-lg bg-[#46BDE1] text-white hover:bg-[#3aa8ca]"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={isLoading}
              className={`ml-0 flex-1 rounded-lg text-white ${
                isLoading ? "bg-red-300" : "bg-red-500 hover:bg-red-600"
              }`}
              onClick={async () => {
                const res = await deleteFile(deleteIndex);
                if (res) {
                  setOpen(false);
                }
              }}
            >
              {isLoading ? (
                <BeatLoader size={10} color={colors.rejected} />
              ) : (
                "Delete"
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {isEmpty && (
        <div
          className={`my-6 w-full rounded-xl border border-dashed border-gray-200 bg-gray-50 py-8 text-center ${
            fileNameHidden ? "text-left" : ""
          }`}
        >
          <Text className="text-sm text-gray-500">No files uploaded yet</Text>
        </div>
      )}
    </div>
  );
};

export default NewDocumentUploader;
